import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { onCLS, onINP, onLCP } from "web-vitals";

import { AuthProvider } from "@/lib/auth";

// Generated by @tanstack/router-plugin from files in src/routes/.
// Run `make frontend-up` (or `npm run dev`) once for the file to be created.
import { routeTree } from "./routeTree.gen";

import "./styles/tokens.css";
import "./styles/global.css";
import "./styles/chrome.css";
import "./styles/pages/landing.css";
import "./styles/pages/onboarding.css";
import "./styles/pages/marketplace.css";
import "./styles/pages/dashboard.css";
import "./styles/pages/access.css";
import "./styles/pages/library.css";
import "./styles/pages/admin.css";
import "./styles/pages/wallet.css";
import "./styles/pages/docs.css";

// ---------------------------------------------------------------------------
// React Query
// ---------------------------------------------------------------------------
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Sensible defaults; tune per-query as you add real endpoints.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// ---------------------------------------------------------------------------
// TanStack Router
// ---------------------------------------------------------------------------
const router = createRouter({
  routeTree,
  // `context` flows into every loader and route component. We pass the
  // QueryClient so route loaders can prefetch via `queryClient.ensureQueryData`.
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

// Register router types globally for type-safe `Link` and `useNavigate`.
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root not found in index.html");
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);

// Core Web Vitals — surface in dev console for now; wire to a beacon later.
if (import.meta.env.DEV) {
  onCLS((metric) => console.debug("[web-vitals] CLS", metric.value));
  onINP((metric) => console.debug("[web-vitals] INP", metric.value));
  onLCP((metric) => console.debug("[web-vitals] LCP", metric.value));
}
